import { SortDirection } from "../sort-direction";
import { formatSortParam, parseSortParam } from "../sort-param";
import { useSortParam } from "./use-sort-param";

/**
 * Field-typed view over the canonical `sort=-field` URL string — the shape the
 * display-options hooks consume. The param is parsed against a closed field
 * set (an absent or unknown field reads as unsorted) and every write goes
 * through the shared codec, so the `-` prefix has exactly one implementation.
 */
export function useSortFields<TField extends string>(
  validFields: readonly TField[],
): {
  sortBy: TField | undefined;
  sortDirection: SortDirection | undefined;
  /** Encode + write; an `undefined` field clears the sort. Direction defaults to ascending. */
  setSortField: (field: TField | undefined, direction?: SortDirection) => void;
  clearSort: () => void;
} {
  const { sort, setSort } = useSortParam();

  const parsed = parseSortParam(sort);
  const known =
    parsed !== undefined &&
    (validFields as readonly string[]).includes(parsed.field);

  const setSortField = (field: TField | undefined, direction?: SortDirection) =>
    setSort(
      field === undefined
        ? undefined
        : formatSortParam(field, direction ?? SortDirection.ASCENDING),
    );

  return {
    // `known` proves membership in `validFields`, so the narrowing cast is safe.
    sortBy: known ? (parsed.field as TField) : undefined,
    sortDirection: known ? parsed.direction : undefined,
    setSortField,
    clearSort: () => setSort(undefined),
  };
}
